/**
 * Income Statement (P&L) report — revenue, COGS, gross profit, operating
 * expenses, net income. Each section expandable to show contributing
 * accounts. Comparison with previous period (delta % column).
 *
 * Data is derived from real entity data via usePersistentMockState:
 *   - accounts       → chart of accounts to classify revenue vs expense
 *   - journalEntries → filter posted entries within the period
 *   - journalLines   → sum balances per account within the period window
 */

import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, FileBarChart } from "lucide-react";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import { KpiCard, cn } from "@bizop/ui-kit";

import { PageHeader } from "../../../../components/PageHeader";
import { usePersistentMockState } from "../../../../lib/usePersistentMockState";
import {
  mockAccounts,
  mockJournalEntries,
  mockJournalLines,
  type MockAccount,
  type MockJournalEntry,
  type MockJournalLine,
  type AccountReportLine,
  type IncomeStatementShape,
} from "../../../../lib/glMock";
import {
  PeriodSelector,
  defaultPeriodWindow,
  type PeriodWindow,
} from "../../../../components/gl/PeriodSelector";
import { ExportButton } from "../../../../components/export";
import {
  downloadCsv,
  exportFilename,
  printElement,
  toCsv,
  type ExportColumn,
} from "../../../../lib/export";

export const Route = createFileRoute(
  "/_app/finance/reports/income-statement",
)({
  component: IncomeStatementPage,
});

/* ── Pure derive function ─────────────────────────────────────── */

function deriveIncomeStatement(
  fromDate: number,
  toDate: number,
  accounts: MockAccount[],
  entries: MockJournalEntry[],
  lines: MockJournalLine[],
): IncomeStatementShape {
  // Build a set of posted entry IDs within the period
  const postedEntryIds = new Set<string>();
  for (const e of entries) {
    if (e.status === "posted") {
      postedEntryIds.add(e._id);
    }
  }

  // Compute balance within the window per account
  const balanceByAccount = new Map<string, bigint>();
  for (const l of lines) {
    if (!postedEntryIds.has(l.journalEntryId)) continue;
    if (l.entryDate < fromDate || l.entryDate > toDate) continue;
    const prev = balanceByAccount.get(l.accountId) ?? 0n;
    balanceByAccount.set(l.accountId, prev + l.debitInBaseCents - l.creditInBaseCents);
  }

  const revenue: AccountReportLine[] = [];
  const cogs: AccountReportLine[] = [];
  const opex: AccountReportLine[] = [];

  for (const acc of accounts) {
    // Only revenue and expense accounts belong in P&L
    if (acc.accountType !== "revenue" && acc.accountType !== "expense") continue;

    const net = balanceByAccount.get(acc._id) ?? 0n;
    const debitNormal = acc.normalBalance === "debit";
    const amt = debitNormal ? net : -net;
    if (amt === 0n) continue;

    const line: AccountReportLine = {
      accountId: acc._id,
      code: acc.code,
      name: acc.name,
      amountInBaseCents: amt,
    };

    if (acc.accountType === "revenue") revenue.push(line);
    else if (acc.code === "5000") cogs.push(line);
    else opex.push(line);
  }

  revenue.sort((a, b) => a.code.localeCompare(b.code));
  cogs.sort((a, b) => a.code.localeCompare(b.code));
  opex.sort((a, b) => a.code.localeCompare(b.code));

  const totalRevenue = revenue.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const totalCogs = cogs.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const totalOpex = opex.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const grossProfit = totalRevenue - totalCogs;

  return {
    fromDate,
    toDate,
    revenue: { accounts: revenue, totalInBaseCents: totalRevenue },
    costOfGoodsSold: { accounts: cogs, totalInBaseCents: totalCogs },
    grossProfitInBaseCents: grossProfit,
    operatingExpenses: { accounts: opex, totalInBaseCents: totalOpex },
    netIncomeInBaseCents: grossProfit - totalOpex,
  };
}

/* ── Component ────────────────────────────────────────────────── */

function IncomeStatementPage() {
  const [period, setPeriod] = useState<PeriodWindow>(defaultPeriodWindow());

  const [accounts] = usePersistentMockState<MockAccount[]>("accounts", mockAccounts);
  const [entries] = usePersistentMockState<MockJournalEntry[]>("journalEntries", mockJournalEntries);
  const [lines] = usePersistentMockState<MockJournalLine[]>("journalLines", mockJournalLines);

  const report = useMemo(
    () => deriveIncomeStatement(period.fromDate, period.toDate, accounts, entries, lines),
    [period.fromDate, period.toDate, accounts, entries, lines],
  );

  // Previous-period comparison: same length window immediately before.
  const previousReport = useMemo(() => {
    const span = period.toDate - period.fromDate;
    const prevTo = period.fromDate - 1;
    const prevFrom = prevTo - span;
    return deriveIncomeStatement(prevFrom, prevTo, accounts, entries, lines);
  }, [period.fromDate, period.toDate, accounts, entries, lines]);

  const printRef = useRef<HTMLDivElement>(null);

  function handleCsv(extension: "csv" | "xls") {
    type IsRow = {
      section: string;
      code: string;
      name: string;
      currentCents: bigint;
      previousCents: bigint;
      deltaPercent: string;
      currency: string;
    };
    const rows: IsRow[] = [];
    function pushSection(
      label: string,
      sectionLines: AccountReportLine[],
      sectionCurrent: bigint,
      sectionPrevious: bigint,
    ) {
      for (const acc of sectionLines) {
        rows.push({
          section: label,
          code: acc.code,
          name: acc.name,
          currentCents: acc.amountInBaseCents,
          previousCents: 0n,
          deltaPercent: "",
          currency: "BDT",
        });
      }
      rows.push({
        section: label,
        code: "",
        name: `Total ${label}`,
        currentCents: sectionCurrent,
        previousCents: sectionPrevious,
        deltaPercent: percentDelta(sectionCurrent, sectionPrevious),
        currency: "BDT",
      });
    }
    pushSection(
      "Revenue",
      report.revenue.accounts,
      report.revenue.totalInBaseCents,
      previousReport.revenue.totalInBaseCents,
    );
    pushSection(
      "Cost of Goods Sold",
      report.costOfGoodsSold.accounts,
      report.costOfGoodsSold.totalInBaseCents,
      previousReport.costOfGoodsSold.totalInBaseCents,
    );
    rows.push({
      section: "Subtotal",
      code: "",
      name: "Gross Profit",
      currentCents: report.grossProfitInBaseCents,
      previousCents: previousReport.grossProfitInBaseCents,
      deltaPercent: percentDelta(
        report.grossProfitInBaseCents,
        previousReport.grossProfitInBaseCents,
      ),
      currency: "BDT",
    });
    pushSection(
      "Operating Expenses",
      report.operatingExpenses.accounts,
      report.operatingExpenses.totalInBaseCents,
      previousReport.operatingExpenses.totalInBaseCents,
    );
    rows.push({
      section: "Subtotal",
      code: "",
      name: "Net Income",
      currentCents: report.netIncomeInBaseCents,
      previousCents: previousReport.netIncomeInBaseCents,
      deltaPercent: percentDelta(
        report.netIncomeInBaseCents,
        previousReport.netIncomeInBaseCents,
      ),
      currency: "BDT",
    });

    const columns: ExportColumn<IsRow>[] = [
      { key: "section", label: "Section" },
      { key: "code", label: "Account code" },
      { key: "name", label: "Name" },
      { key: "currentCents", label: "Current period" },
      { key: "previousCents", label: "Previous period" },
      { key: "deltaPercent", label: "Delta %" },
      { key: "currency", label: "Currency" },
    ];
    downloadCsv(
      exportFilename("income-statement", extension),
      toCsv(rows, columns),
    );
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: "Income Statement",
      subtitle: `${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}`,
    });
  }

  return (
    <div className="flex flex-col gap-6" ref={printRef}>
      <PageHeader
        title="Income Statement"
        description={`P&L for ${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}.`}
        actions={
          <ExportButton
            onCsv={() => handleCsv("csv")}
            onExcel={() => handleCsv("xls")}
            onPrint={handlePrint}
          />
        }
      />

      <PeriodSelector value={period} onChange={setPeriod} />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Income KPIs"
      >
        <KpiCard
          caption="Revenue"
          value={formatMoneySmart(report.revenue.totalInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={deltaText(
            report.revenue.totalInBaseCents,
            previousReport.revenue.totalInBaseCents,
          )}
        />
        <KpiCard
          caption="Gross profit"
          value={formatMoneySmart(report.grossProfitInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={deltaText(
            report.grossProfitInBaseCents,
            previousReport.grossProfitInBaseCents,
          )}
        />
        <KpiCard
          caption="Operating expenses"
          value={formatMoneySmart(
            report.operatingExpenses.totalInBaseCents,
            "BDT",
            { locale: "en-BD" },
          )}
          hint={deltaText(
            report.operatingExpenses.totalInBaseCents,
            previousReport.operatingExpenses.totalInBaseCents,
          )}
        />
        <KpiCard
          caption="Net income"
          value={formatMoneySmart(report.netIncomeInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={deltaText(
            report.netIncomeInBaseCents,
            previousReport.netIncomeInBaseCents,
          )}
        />
      </section>

      <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-(--bg-surface) text-(--text-tertiary) text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left w-2/3">Section</th>
              <th className="px-3 py-2 text-right">Current</th>
              <th className="px-3 py-2 text-right">Previous</th>
              <th className="px-3 py-2 text-right">Δ %</th>
            </tr>
          </thead>
          <tbody>
            <Section
              label="Revenue"
              accounts={report.revenue.accounts}
              total={report.revenue.totalInBaseCents}
              prev={previousReport.revenue.totalInBaseCents}
            />
            <Section
              label="Cost of Goods Sold"
              accounts={report.costOfGoodsSold.accounts}
              total={report.costOfGoodsSold.totalInBaseCents}
              prev={previousReport.costOfGoodsSold.totalInBaseCents}
              isNegative
            />
            <SubtotalRow
              label="Gross Profit"
              total={report.grossProfitInBaseCents}
              prev={previousReport.grossProfitInBaseCents}
            />
            <Section
              label="Operating Expenses"
              accounts={report.operatingExpenses.accounts}
              total={report.operatingExpenses.totalInBaseCents}
              prev={previousReport.operatingExpenses.totalInBaseCents}
              isNegative
            />
            <SubtotalRow
              label="Net Income"
              total={report.netIncomeInBaseCents}
              prev={previousReport.netIncomeInBaseCents}
              emphatic
            />
          </tbody>
        </table>
      </div>

      {report.revenue.totalInBaseCents === 0n &&
      report.operatingExpenses.totalInBaseCents === 0n ? (
        <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-8 text-center text-[13px] text-(--text-secondary)">
          <FileBarChart
            className="mx-auto mb-2 h-6 w-6 text-(--text-tertiary)"
            aria-hidden
          />
          No posted activity in this window.
        </div>
      ) : null}
    </div>
  );
}

function Section({
  label,
  accounts,
  total,
  prev,
  isNegative,
}: {
  label: string;
  accounts: AccountReportLine[];
  total: bigint;
  prev: bigint;
  isNegative?: boolean;
}) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <tr
        className="border-t border-(--border-subtle) bg-(--bg-canvas) cursor-pointer hover:bg-(--bg-hover)"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="px-3 py-2 font-semibold text-(--text-primary)">
          <span className="inline-flex items-center gap-1">
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {label}
          </span>
        </td>
        <td
          className={cn(
            "px-3 py-2 text-right font-mono tabular-nums font-semibold",
            isNegative ? "text-(--text-secondary)" : "text-(--text-primary)",
          )}
        >
          {formatMoney(total, "BDT", { locale: "en-BD" })}
        </td>
        <td className="px-3 py-2 text-right font-mono tabular-nums text-(--text-tertiary)">
          {formatMoney(prev, "BDT", { locale: "en-BD" })}
        </td>
        <td className="px-3 py-2 text-right font-mono tabular-nums text-(--text-secondary)">
          {percentDelta(total, prev)}
        </td>
      </tr>
      {open
        ? accounts.map((acc) => (
            <tr
              key={acc.accountId}
              className="border-t border-(--border-subtle) bg-(--bg-surface)"
            >
              <td className="px-3 py-1.5 pl-9">
                <span className="font-mono mr-2 text-[12px] text-(--text-secondary)">
                  {acc.code}
                </span>
                <span className="text-[12px] text-(--text-primary)">
                  {acc.name}
                </span>
              </td>
              <td className="px-3 py-1.5 text-right font-mono tabular-nums text-[12px] text-(--text-secondary)">
                {formatMoney(acc.amountInBaseCents, "BDT", { locale: "en-BD" })}
              </td>
              <td className="px-3 py-1.5" />
              <td className="px-3 py-1.5" />
            </tr>
          ))
        : null}
    </>
  );
}

function SubtotalRow({
  label,
  total,
  prev,
  emphatic,
}: {
  label: string;
  total: bigint;
  prev: bigint;
  emphatic?: boolean;
}) {
  return (
    <tr
      className={cn(
        "border-t-2 border-(--border-subtle)",
        emphatic ? "bg-(--bg-selected)" : "bg-(--bg-surface)",
      )}
    >
      <td className="px-3 py-2 font-semibold text-(--text-primary)">{label}</td>
      <td
        className={cn(
          "px-3 py-2 text-right font-mono tabular-nums font-semibold",
          total < 0n ? "text-(--danger)" : "text-(--text-primary)",
        )}
      >
        {formatMoney(total, "BDT", { locale: "en-BD" })}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-(--text-tertiary)">
        {formatMoney(prev, "BDT", { locale: "en-BD" })}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-(--text-secondary)">
        {percentDelta(total, prev)}
      </td>
    </tr>
  );
}

function percentDelta(curr: bigint, prev: bigint): string {
  if (prev === 0n) return curr === 0n ? "0%" : "—";
  const change = Number((curr - prev) * 1000n) / 1000;
  const base = Number(prev < 0n ? -prev : prev) / 100;
  if (base === 0) return "—";
  const pct = (change / 100 / base) * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

function deltaText(curr: bigint, prev: bigint): string {
  return `vs prev: ${percentDelta(curr, prev)}`;
}
